import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Badge, Button, Form, Modal, Card, Row, Col } from 'react-bootstrap';
import Loader from '../../component/loader';
import { FiArrowLeft, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { TbTruckReturn } from "react-icons/tb";
import { BsCheckCircleFill, BsImage, BsCameraVideo } from "react-icons/bs";
import axios from "axios";
import Cookies from "js-cookie";
import { decrpt } from "../../component/encryption";
import moment from "moment";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function ReturnOrderSingle() {
  const location = useLocation();
  const navigate = useNavigate();
  const returnOrderId = location?.state?.item;

  const [loader, setLoader] = useState(false);
  const [returnOrder, setReturnOrder] = useState(null);
  const [selectedStage, setSelectedStage] = useState("1");
  const [deniedReason, setDeniedReason] = useState("");
  const [remarks, setRemarks] = useState("");
  const [previewImage, setPreviewImage] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [isDiscountsOpen, setIsDiscountsOpen] = useState(true);

  const fetchDetails = async () => {
    if (!returnOrderId) {
      navigate('/return-orders');
      return;
    }
    setLoader(true);
    try {
      const token = decrpt(Cookies.get('s_l'));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await axios.post(`/admin/return/orders/details`, { id: returnOrderId });
      if (response.data && response.data.data) {
        const data = response.data.data;
        setReturnOrder(data);
        setSelectedStage(String(data.status || "1"));
        setDeniedReason(data.denied_reason || "");
        setRemarks(data.remarks || "");
      }
      setLoader(false);
    } catch (err) {
      console.log('Fetch return details error', err);
      toast.error("Failed to load return details", { hideProgressBar: true });
      setLoader(false);
    }
  };

  const handleStageUpdate = async (e) => {
    e.preventDefault();
    if (selectedStage === "5" && !deniedReason.trim()) {
      toast.warn("Please enter a reason for denying the return request", { hideProgressBar: true });
      return;
    }

    setUpdating(true);
    try {
      const token = decrpt(Cookies.get('s_l'));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const payload = {
        id: returnOrderId,
        status: selectedStage,
        denied_reason: selectedStage === "5" ? deniedReason : null,
        remarks: remarks,
      };

      const res = await axios.post(`/admin/return/orders/status/update`, payload);
      if (res.data && res.data.success) {
        toast.success("Return stage updated successfully!", { hideProgressBar: true });
        setReturnOrder(res.data.data);
      }
      setUpdating(false);
    } catch (err) {
      console.log('Update return stage error', err);
      toast.error(err.response?.data?.message || "Failed to update return stage", { hideProgressBar: true });
      setUpdating(false);
    }
  };

  const calculatePaidAmount = (item) => {
    if (!item) return 0;
    const base = Number(item.amount || 0);
    const gst = Number(item.product?.gst || 0);
    if (gst > 0 && Math.abs(base - Number(item.product?.price || 0)) < 0.05) {
      return Number((base + (base * gst / 100)).toFixed(2));
    }
    return base;
  };

  const getStageBadge = (status) => {
    switch (String(status)) {
      case '1':
        return <Badge bg="warning" className="text-dark fs-6 px-3 py-2">Stage: Requested (New)</Badge>;
      case '2':
        return <Badge bg="info" className="text-white fs-6 px-3 py-2">Stage: Approved</Badge>;
      case '3':
        return <Badge bg="primary" className="fs-6 px-3 py-2">Stage: Product Picked Up</Badge>;
      case '4':
        return <Badge bg="success" className="fs-6 px-3 py-2">Stage: Refunded / Completed</Badge>;
      case '5':
        return <Badge bg="danger" className="fs-6 px-3 py-2">Stage: Denied</Badge>;
      default:
        return <Badge bg="secondary" className="fs-6 px-3 py-2">Stage: Pending</Badge>;
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchDetails();
  }, [returnOrderId]);

  return (
    <div className="main-content">
      {loader || !returnOrder ? (
        <Loader />
      ) : (
        <>
          <ToastContainer position="top-center" autoClose={3000} hideProgressBar={false} theme={'colored'} />
          <div className="section">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => navigate('/return-orders')}
                  className="d-flex align-items-center gap-1"
                >
                  <FiArrowLeft /> Back to List
                </Button>
                <h5 className="mb-0">
                  <TbTruckReturn className="me-1" /> Return Request #{returnOrder.id}
                </h5>
              </div>
              <div>{getStageBadge(returnOrder.status)}</div>
            </div>

            <Row className="g-3">
              {/* Left Column: Product & Return Evidence */}
              <Col lg={8}>
                {/* Product Info Card */}
                <Card className="p-3 mb-3 border-0 shadow-sm">
                  <h6 className="fw-bold border-bottom pb-2 text-primary">Item Details</h6>
                  <div className="d-flex gap-3 align-items-start mt-2">
                    {(() => {
                      const rawImg = returnOrder.product_image || returnOrder.product?.main_image;
                      if (!rawImg) return null;
                      const imgSrc = rawImg.startsWith('http')
                        ? rawImg
                        : `https://backend.aivimart.com/images/${rawImg}`;

                      return (
                        <img
                          src={imgSrc}
                          alt=""
                          onError={(e) => {
                            if (!e.target.dataset.triedPublic) {
                              e.target.dataset.triedPublic = "true";
                              e.target.src = `https://backend.aivimart.com/public/images/${rawImg}`;
                            }
                          }}
                          style={{ width: '90px', height: '90px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                        />
                      );
                    })()}
                    <div className="flex-grow-1">
                      <h5 className="fw-bold mb-1">{returnOrder.product?.name}</h5>
                      <div className="text-muted small mb-1">
                        Order ID: <strong>{returnOrder.order?.order_uuid || `#${returnOrder.order_id}`}</strong> |
                        Ordered On: {moment(returnOrder.order?.order_date || returnOrder.order?.created_at).format("DD MMM, YYYY")}
                      </div>
                      <div className="d-flex gap-3 flex-wrap mt-2">
                        {returnOrder.product_size?.name && (
                          <span className="badge bg-light text-dark border">
                            Size: {returnOrder.product_size.name}
                          </span>
                        )}
                        <span className="badge bg-light text-dark border">
                          Amount: <strong>₹{calculatePaidAmount(returnOrder).toFixed(2)}</strong>
                        </span>
                        <span className="badge bg-light text-dark border">
                          Delivered On: {returnOrder.delivered_date ? moment(returnOrder.delivered_date).format("DD MMM, YYYY") : (returnOrder.order?.delivered_date || "Delivered")}
                        </span>
                        {returnOrder.policy?.return_policy && (
                          <span className="badge bg-light text-dark border">
                            Policy: {returnOrder.policy.return_policy}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>

                {/* Return Reason & Description */}
                <Card className="p-3 mb-3 border-0 shadow-sm">
                  <h6 className="fw-bold border-bottom pb-2 text-primary">Customer Return Reason</h6>
                  <div className="bg-light p-3 rounded mb-3">
                    <div className="fw-bold text-dark fs-6 mb-1">
                      Reason: <span className="text-danger">{returnOrder.return_reason || returnOrder.reason?.reason || "Not specified"}</span>
                    </div>
                    {returnOrder.description && (
                      <div className="text-muted mt-2">
                        <strong>Customer Description:</strong>
                        <p className="mb-0 mt-1" style={{ whiteSpace: 'pre-wrap' }}>{returnOrder.description}</p>
                      </div>
                    )}
                    {returnOrder.remarks && returnOrder.remarks !== returnOrder.description && (
                      <div className="text-muted mt-2">
                        <strong>Remarks:</strong>
                        <p className="mb-0 mt-1">{returnOrder.remarks}</p>
                      </div>
                    )}
                  </div>

                  {/* Photo Evidence */}
                  <h6 className="fw-bold mt-2 d-flex align-items-center gap-1">
                    <BsImage /> Photo Evidence ({returnOrder.images_url ? returnOrder.images_url.length : 0})
                  </h6>
                  {returnOrder.images_url && returnOrder.images_url.length > 0 ? (
                    <div className="d-flex gap-2 flex-wrap mt-2">
                      {returnOrder.images_url.map((imgUrl, i) => (
                        <div
                          key={i}
                          onClick={() => setPreviewImage(imgUrl)}
                          style={{
                            cursor: 'pointer',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            overflow: 'hidden',
                            width: '100px',
                            height: '100px'
                          }}
                        >
                          <img
                            src={imgUrl}
                            alt={`evidence-${i}`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted small mb-3">No photo evidence uploaded.</p>
                  )}

                  {/* Video Evidence */}
                  <h6 className="fw-bold mt-3 d-flex align-items-center gap-1">
                    <BsCameraVideo /> Video Evidence
                  </h6>
                  {returnOrder.video_url ? (
                    <div className="mt-2" style={{ maxWidth: '480px' }}>
                      <video
                        controls
                        src={returnOrder.video_url}
                        style={{ width: '100%', maxHeight: '280px', borderRadius: '8px', background: '#000' }}
                      />
                    </div>
                  ) : (
                    <p className="text-muted small mb-0">No video evidence uploaded.</p>
                  )}
                </Card>

                {/* Refund Bank Details */}
                <Card className="p-3 mb-3 border-0 shadow-sm">
                  <h6 className="fw-bold border-bottom pb-2 text-primary">Customer Refund Bank Details</h6>
                  {returnOrder.user_bank ? (
                    <Row className="g-2 text-sm mt-1">
                      <Col sm={6}>
                        <span className="text-muted d-block small">Account Holder Name</span>
                        <strong>{returnOrder.user_bank.name || "-"}</strong>
                      </Col>
                      <Col sm={6}>
                        <span className="text-muted d-block small">Bank Name</span>
                        <strong>{returnOrder.user_bank.bnk_name || "-"}</strong>
                      </Col>
                      <Col sm={6}>
                        <span className="text-muted d-block small">Account Number</span>
                        <strong className="font-monospace">{returnOrder.user_bank.ac_no || "-"}</strong>
                      </Col>
                      <Col sm={6}>
                        <span className="text-muted d-block small">IFSC Code</span>
                        <strong className="font-monospace">{returnOrder.user_bank.ifsc || "-"}</strong>
                      </Col>
                      <Col sm={6}>
                        <span className="text-muted d-block small">Branch</span>
                        <span>{returnOrder.user_bank.branch_name || "-"}</span>
                      </Col>
                      <Col sm={6}>
                        <span className="text-muted d-block small">Refund Payment Status</span>
                        {returnOrder.payment_status == 2 ? (
                          <Badge bg="success">Payment Refunded</Badge>
                        ) : (
                          <Badge bg="warning" className="text-dark">Pending Refund</Badge>
                        )}
                      </Col>
                    </Row>
                  ) : (
                    <p className="text-muted small mb-0">No bank details recorded.</p>
                  )}
                </Card>
              </Col>

              {/* Right Column: Stage Update & Customer / Seller Info */}
              <Col lg={4}>
                {/* Stage Update Box */}
                <Card className="p-3 mb-3 border-0 shadow-sm border-top border-4 border-primary">
                  <h6 className="fw-bold pb-2 border-bottom">Update Return Stage</h6>
                  <Form onSubmit={handleStageUpdate}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Current Stage</Form.Label>
                      <Form.Select
                        value={selectedStage}
                        onChange={(e) => setSelectedStage(e.target.value)}
                        className="form-select"
                      >
                        <option value="1">1. Requested (New)</option>
                        <option value="2">2. Approved (Accepted)</option>
                        <option value="3">3. Product Picked Up</option>
                        <option value="4">4. Refunded (Completed)</option>
                        <option value="5">5. Denied (Rejected)</option>
                      </Form.Select>
                      <Form.Text className="text-muted small">
                        Updating to <strong>Refunded</strong> automatically sets refund status as paid.
                      </Form.Text>
                    </Form.Group>

                    {selectedStage === "5" && (
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold text-danger">Reason for Denial <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          placeholder="Explain why this return request is being denied..."
                          value={deniedReason}
                          onChange={(e) => setDeniedReason(e.target.value)}
                          required
                        />
                      </Form.Group>
                    )}

                    <Button
                      type="submit"
                      variant="primary"
                      className="w-100 fw-bold"
                      disabled={updating}
                    >
                      {updating ? "Updating..." : "Save Stage Changes"}
                    </Button>
                  </Form>
                </Card>

                {/* Price Details Card */}
                {(() => {
                  const paidAmount = calculatePaidAmount(returnOrder);
                  const basePrice = Number(returnOrder.product?.price || 0);
                  const gstRate = Number(returnOrder.product?.gst || 0);
                  const gstAmount = gstRate > 0 ? Number(((basePrice * gstRate) / 100).toFixed(2)) : 0;
                  const itemTotal = (basePrice + gstAmount > 0) ? Number((basePrice + gstAmount).toFixed(2)) : paidAmount;

                  const rawMrp = Number(returnOrder.product?.mrp || returnOrder.mrp || 0);
                  const mrp = rawMrp > itemTotal ? rawMrp : itemTotal;
                  const discountOnMrp = Math.max(0, Number((mrp - itemTotal).toFixed(2)));
                  const shippingCharge = Number(returnOrder.order?.shipping_charge || 0);
                  const overallPrice = Number(returnOrder.order?.overall_price || paidAmount);

                  return (
                    <Card className="p-3 mb-3 border-0 shadow-sm border-top border-4 border-danger">
                      <div className="border-bottom pb-2 mb-3">
                        <h6 className="fw-bold mb-0 text-dark">Price Details</h6>
                      </div>

                      <div className="small">
                        {/* MRP */}
                        <div className="d-flex justify-content-between text-muted mb-2">
                          <span title="Maximum Retail Price (incl. of all taxes)">MRP (incl. of all taxes)</span>
                          <span className="fw-semibold text-dark">₹{mrp.toFixed(2)}</span>
                        </div>

                        {/* Discounts */}
                        {discountOnMrp > 0 && (
                          <div className="border-top border-dashed py-2 mb-1">
                            <div
                              className="d-flex justify-content-between align-items-center fw-semibold text-dark"
                              onClick={() => setIsDiscountsOpen(!isDiscountsOpen)}
                              style={{ cursor: 'pointer', userSelect: 'none' }}
                            >
                              <span>Discounts</span>
                              <span className="text-muted">
                                {isDiscountsOpen ? <FiChevronUp /> : <FiChevronDown />}
                              </span>
                            </div>
                            {isDiscountsOpen && (
                              <div className="mt-2 ps-2 border-start border-2 border-success">
                                <div className="d-flex justify-content-between text-success mb-1">
                                  <span className="text-muted">Discount on MRP</span>
                                  <span>− ₹{discountOnMrp.toFixed(2)}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Item Base & GST Breakdown */}
                        <div className="bg-light p-2 rounded mb-2">
                          <div className="d-flex justify-content-between text-muted mb-1">
                            <span>Item Base Price</span>
                            <span>₹{basePrice.toFixed(2)}</span>
                          </div>
                          {gstRate > 0 && (
                            <div className="d-flex justify-content-between text-muted mb-1">
                              <span>GST ({gstRate}%)</span>
                              <span>+ ₹{gstAmount.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="d-flex justify-content-between fw-semibold text-dark pt-1 border-top border-secondary border-opacity-10">
                            <span>Subtotal (1 Item)</span>
                            <span>₹{itemTotal.toFixed(2)}</span>
                          </div>
                        </div>

                        {/* Shipping Fee */}
                        <div className="d-flex justify-content-between text-muted mb-2">
                          <span>Shipping Fee</span>
                          {shippingCharge === 0 ? (
                            <span className="text-success fw-bold">FREE</span>
                          ) : (
                            <span className="text-dark fw-semibold">₹{shippingCharge.toFixed(2)}</span>
                          )}
                        </div>

                        <hr className="my-2" />

                        {/* Total Order Amount */}
                        <div className="d-flex justify-content-between align-items-baseline mb-2">
                          <span className="fw-bold text-dark">Total Amount</span>
                          <span className="fw-bold text-danger fs-5">₹{overallPrice.toFixed(2)}</span>
                        </div>

                        {/* Refund Amount */}
                        <div className="p-2 rounded bg-primary bg-opacity-10 border border-primary border-opacity-25 d-flex justify-content-between align-items-center">
                          <div>
                            <span className="d-block fw-bold text-primary small">Refund Amount</span>
                            <span className="text-muted" style={{ fontSize: '11px' }}>
                              {returnOrder.payment_status == 2 ? 'Payment Refunded' : 'Payable to Customer'}
                            </span>
                          </div>
                          <span className="fs-5 fw-bold text-primary">₹{paidAmount.toFixed(2)}</span>
                        </div>
                      </div>
                    </Card>
                  );
                })()}

                {/* Customer Details */}
                <Card className="p-3 mb-3 border-0 shadow-sm">
                  <h6 className="fw-bold pb-2 border-bottom text-secondary">Customer Information</h6>
                  <div className="small space-y-1">
                    <div><strong>Name:</strong> {returnOrder.user?.name || "N/A"}</div>
                    <div><strong>Mobile:</strong> {returnOrder.user?.mobile || "N/A"}</div>
                    <div><strong>Email:</strong> {returnOrder.user?.email || "N/A"}</div>
                  </div>
                </Card>

                {/* Seller Details */}
                <Card className="p-3 mb-3 border-0 shadow-sm">
                  <h6 className="fw-bold pb-2 border-bottom text-secondary">Seller Information</h6>
                  <div className="small space-y-1">
                    <div><strong>Seller:</strong> {returnOrder.seller?.name || "N/A"}</div>
                    <div><strong>Mobile:</strong> {returnOrder.seller?.mobile || "N/A"}</div>
                  </div>
                </Card>

                {/* Shipping Address */}
                {returnOrder.order?.shipping_address && (
                  <Card className="p-3 border-0 shadow-sm">
                    <h6 className="fw-bold pb-2 border-bottom text-secondary">Pickup / Shipping Address</h6>
                    <div className="small text-muted">
                      <div className="fw-semibold text-dark">{returnOrder.order.shipping_address.name}</div>
                      <div>{returnOrder.order.shipping_address.street}</div>
                      <div>{returnOrder.order.shipping_address.address}</div>
                      <div>
                        {returnOrder.order.shipping_address.city_name}, {returnOrder.order.shipping_address.state_name} - {returnOrder.order.shipping_address.pin}
                      </div>
                      <div>Phone: {returnOrder.order.shipping_address.mobile}</div>
                    </div>
                  </Card>
                )}
              </Col>
            </Row>

            {/* Photo Zoom Modal */}
            <Modal show={!!previewImage} onHide={() => setPreviewImage(null)} size="lg" centered>
              <Modal.Header closeButton>
                <Modal.Title className="h6">Photo Evidence</Modal.Title>
              </Modal.Header>
              <Modal.Body className="text-center p-2">
                {previewImage && (
                  <img
                    src={previewImage}
                    alt="preview"
                    style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain' }}
                  />
                )}
              </Modal.Body>
            </Modal>
          </div>
        </>
      )}
    </div>
  );
}

export default ReturnOrderSingle;

